import { proxy } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'

import type { Column } from '~shared/rundown/column'
type ColumnShareLinkState = {
  /**
   * Columns the link hides (`?hiddenColumns=`). The guest cannot show them
   * again locally.
   */
  hiddenColumns: Column['id'][]

  /** Columns a partial editor may edit (`?edit-columns=`). */
  editableColumns: Column['id'][]
}

const initialState: ColumnShareLinkState = {
  hiddenColumns: [],
  editableColumns: [],
}

/**
 * Column settings of the share link the rundown was opened with.
 *
 * Read-only config, not UI state: chosen in the "Invite Guests" modal, signed
 * together with the URL, and fixed for the whole session. The session is the
 * only writer, once on start and once on stop.
 *
 * @remarks
 *   `editableColumns` is enforced here on the client. The server only checks
 *   whether the list is empty, to grant `PARTIAL_EDITOR` (see
 *   `permissionsFromUrl` in functions). `hiddenColumns` is not a permission at
 *   all: the column data still reaches the guest.
 */
const columnShareLinkStore = proxy<ColumnShareLinkState>(
  deepClone(initialState),
)

export const columnShareLinkActions = {
  /** Called when a rundown session starts. */
  set: (state: ColumnShareLinkState) => {
    columnShareLinkStore.hiddenColumns = [...state.hiddenColumns]
    columnShareLinkStore.editableColumns = [...state.editableColumns]
  },

  /** Called when leaving a rundown. */
  resetStore: () => {
    resetStore(columnShareLinkStore, initialState)
  },
}

/**
 * The store itself, for the derived layer, `useColumnAccess` and non-React
 * readers.
 *
 * Read-only by type, so writes have to go through the actions above.
 */
export const columnShareLinkReadonly: Readonly<ColumnShareLinkState> =
  columnShareLinkStore

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(columnShareLinkStore, {
    name: 'columnShareLinkStore',
    enabled: true,
  })
}
// #endregion
